// Manual sections: what happens once a call has been assessed — disputes and the
// audit trail. See ../index.js for the schema.

const afterReview = [
  {
    id: 'disputes',
    group: 'After the review',
    title: 'Disputes',
    lede: 'From the agent’s objection to the recorded outcome.',
    roles: ['sAdm', 'adm', 'qa', 'callAgent'],
    blocks: [
      { type: 'h3', text: 'Raising one — the call agent’s side' },
      {
        type: 'steps',
        items: [
          {
            title: 'Open the call from **My Calls**.',
            text: 'My Calls lists only calls that have been assessed, so every call there can be disputed.',
          },
          {
            title: 'Choose `Dispute`.',
            text: 'In the **Dispute Assessment** box, explain what you believe is wrong, then choose `Submit Dispute`. A reason is required.',
          },
          {
            title: 'Wait for the review.',
            text:
              'The call shows `Disputed — pending review` until a reviewer decides. Then it shows `Dispute Resolved — Assessment Changed` or `Dispute Resolved — Assessment Upheld`, with the reviewer’s reason underneath.',
          },
        ],
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'One dispute per call',
        text:
          'A call can be disputed once. If `Dispute` is not there, the call has already been disputed. You can dispute only your own calls.',
      },
      {
        type: 'p',
        text:
          'A **General Manager** (an agent whose role is GM in **Agent Management**, with a team attached) can also dispute the calls of the agents on their team. On My Calls, pick the agent under `Viewing calls for`, open the call and dispute it the same way. Calls of agents outside the team are refused.',
      },
      { type: 'h3', text: 'The Disputes page — the reviewer’s side' },
      {
        type: 'p',
        text:
          'Super Admins and Admins find **Disputes** under **Administration**; QA Agents have it in their menu. Any of them can resolve any dispute. Switch between `Pending`, `Resolved` and `All`; the number in brackets counts the disputes loaded on the page. Expand a row for the reason, who disputed it and when, and, once resolved, the outcome, reason, resolver and time. `Refresh` reloads the list.',
      },
      {
        type: 'p',
        text:
          'Every resolution needs a written **Reason**, which the call agent sees on their call. Confirm it with `Mark Reviewed`.',
      },
      { type: 'h3', text: 'If the assessment was right' },
      {
        type: 'steps',
        items: [
          {
            title: 'Choose `No Change Needed`',
            text:
              '— on the Disputes page row or on the call itself. Give your reason and choose `Mark Reviewed`. The assessment is left exactly as it was and the dispute shows as `Resolved — Upheld`.',
          },
        ],
      },
      { type: 'h3', text: 'If the assessment needs changing' },
      {
        type: 'steps',
        items: [
          { title: 'Open the call with `View Call`.', text: '' },
          {
            title: 'Choose `Change Assessment`',
            text: 'and confirm. The call is reopened and opens straight into editing.',
          },
          {
            title: 'Make your corrections and choose `Save Changes`',
            text: 'as you would for any override — see {{Reviewing a call|#call-details}}.',
          },
          {
            title: 'Choose `Finish Resolving Dispute`',
            text:
              'give your reason and choose `Mark Reviewed`. The call is locked as assessed again and the dispute shows as `Resolved — Changed`.',
          },
        ],
      },
      {
        type: 'note',
        variant: 'tip',
        tag: 'Finish Resolving Dispute is missing?',
        text:
          'It appears only after you have saved a change. Until then the call reads "Make your changes below, then click Save Changes to continue resolving this dispute." `Mark as Assessed` is hidden while a dispute is open, so the dispute is always closed with a decision.',
      },
      {
        type: 'p',
        text:
          'Call lists that show the **Disputed** column mark each call `Disputed`, `Resolved — Changed` or `Resolved — Upheld`. Raising and resolving a dispute each add a record to the {{Audit|#audit}} log.',
      },
    ],
  },

  {
    id: 'audit',
    group: 'After the review',
    title: 'Audit',
    lede: 'Changes to calls: who made them, when, and the values before and after.',
    roles: ['sAdm', 'adm'],
    blocks: [
      {
        type: 'p',
        text: 'Find it under **Administration › Audit**. It is the override history across campaigns and scorecards.',
      },
      {
        type: 'p',
        text:
          'A record is written each time a reviewer saves corrections to a call’s verdicts, a call agent raises a dispute, a reviewer resolves one, or a Super Admin edits a call’s details (agent, conversation time, conversation ID or DNIS). Repeated changes to one call are kept as separate records.',
      },
      { type: 'h3', text: 'The list' },
      {
        type: 'ul',
        items: [
          'Each card shows the agent and call time (or, for an assignment, who it was assigned to and the job), the email of the person who made the change, the campaign and scorecard, and when it happened.',
          'The **Fields** line lists the questions or fields the record covers. For score corrections, a badge counts how many details changed.',
          '`View changes` opens the record.',
          'The list loads 20 records at a time; `Load More` fetches the next 20.',
        ],
      },
      { type: 'h3', text: 'Finding a record' },
      {
        type: 'ul',
        items: [
          '`Query Specific Item` fetches one call’s records by its **exact filename**, optionally between a `Start Timestamp` and an `End Timestamp`. Choose `Query Item`; `Clear Query` returns to the full list. The filename is shown in small grey text on a record’s detail page.',
          '`Filter Loaded Items` narrows only what is already loaded, by `Username (Email)`, `Campaign` or `Template`. Load more records first if what you want is older. `Clear All Filters` removes them.',
        ],
      },
      { type: 'h3', text: 'Reading a record' },
      {
        type: 'p',
        text:
          'The detail page names who made the change and when ("Overridden by …"). **Old Values** are on the left and **New Values** on the right, one row per question or field. Changed rows are highlighted and tagged `Changed`, and changed words are marked within the text.',
      },
      {
        type: 'p',
        text:
          'A question counts as changed when its status, score, answer, reason or pre-check steps differ. Edited evidence quotes are marked in the quote but do not, on their own, tag the question `Changed`.',
      },
    ],
  },
];

export default afterReview;
